import * as THREE from "three/webgpu";
import {
    attribute, float,
    Fn,
    instancedBufferAttribute,
    mod,
    oneMinus,
    sin,
    smoothstep,
    uniform,
    uv,
    vec2,
    vec3
} from "three/tsl";
import Experience from "../Experience.js";

export default class DustParticles {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.debug = this.experience.debug;

        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('Dust particles');
        }

        this.properties = {
            volume: new THREE.Vector3(20, 20, 20),
            count: 50,
            color: '#ff941a',
            size: 0.1
        }

        this.setGeometry();
        this.setMaterial();
        this.setMesh();
        this.setDebug();

        this.time.on('tick', () => {
            this.update();
        })
    }

    update() {
        this.uTime.value = this.time.elapsed * 0.001;
    }

    setGeometry() {
        const vertices = new Float32Array(3 * this.properties.count);
        const lifetimes = new Float32Array(this.properties.count);
        // 1. Add a time offset array
        const timeOffsets = new Float32Array(this.properties.count);
        // Add a randoms array for unique movement multipliers
        const randoms = new Float32Array(3 * this.properties.count);

        const volume = this.properties.volume;
        for (let i = 0; i < this.properties.count; i++) {
            const i3 = i * 3;
            vertices[i3 + 0] = THREE.MathUtils.randFloatSpread(volume.x);
            vertices[i3 + 1] = Math.abs(THREE.MathUtils.randFloatSpread(volume.y)) + this.properties.size * 0.5;

            // Shift such that more particles are in front of the camera
            vertices[i3 + 2] = THREE.MathUtils.randFloatSpread(volume.z) + volume.z * 0.25;

            lifetimes[i] = THREE.MathUtils.randFloat(3, 6);
            timeOffsets[i] = THREE.MathUtils.randFloat(0, 10);
            
            // Generate random values to differentiate particle movements
            randoms[i3 + 0] = THREE.MathUtils.randFloat(-1.5, 1.5); // X sway direction and amplitude
            randoms[i3 + 1] = THREE.MathUtils.randFloat(0.2, 1.5);  // Y floating speed multiplier
            randoms[i3 + 2] = THREE.MathUtils.randFloat(-1.5, 1.5); // Z sway direction and amplitude
        }

        this.positions = instancedBufferAttribute(new THREE.InstancedBufferAttribute(vertices, 3));
        this.lifetimes = new THREE.InstancedBufferAttribute(lifetimes, 1);
        this.timeOffsets = new THREE.InstancedBufferAttribute(timeOffsets, 1);
        this.randoms = new THREE.InstancedBufferAttribute(randoms, 3);
    }

    setMaterial() {
        this.uSize = uniform(this.properties.size);
        this.uColor = uniform(new THREE.Color(this.properties.color));
        this.uTime = uniform(this.time.elapsed);

        this.material = new THREE.PointsNodeMaterial({
            positionNode: this.positions,
            sizeNode: this.uSize,
            colorNode: this.uColor,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });

        // Define attributes
        const lifetime = attribute('lifetime', 'float');
        const timeOffset = attribute('timeOffset', 'float');
        const randomVec = attribute('randoms', 'vec3'); // Read our new randoms array

        // 3. Add the unique offset to the global time so they don't sync up
        const particleTime = this.uTime.add(timeOffset);

        // Calculate the particle lifecycle progress using mod
        const time = mod(particleTime, lifetime).div(lifetime); // Time normalized between 0.0 and 1.0

        // Define fade-in and fade-out phases
        const fadeIn = smoothstep(0.0, 0.3, time); // Fade in during the first 30% of the lifetime
        const fadeOut = smoothstep(0.0, 0.5, oneMinus(time)); 

        // Apply fadeIn and fadeOut to the color node
        this.material.colorNode = this.uColor.mul(fadeIn.mul(fadeOut));

        // Define a simple circle gradient for the alpha map
        const alphaMap = oneMinus(uv().sub(vec2(0.5)).length().mul(2.0));

        // Combine alpha fading with the gradient alpha map
        this.material.opacityNode = fadeIn.mul(fadeOut).mul(alphaMap);

        // 4. Create a gentle movement using sin and the normalized lifetime
        // Multiply by randomVec to break uniformity
        const movement = vec3(
            sin(particleTime.mul(0.4)).mul(0.5).mul(randomVec.x),                     // Unique X sway
            time.mul(0.5).mul(randomVec.y).add(sin(particleTime.mul(0.2)).mul(0.2)),  // Unique Y float speed
            sin(particleTime.mul(0.6)).mul(0.5).mul(randomVec.z)                     // Unique Z sway
        );

        // Apply movement offset to the original positions on the GPU
        this.material.positionNode = this.positions.add(movement);
    }

    setMesh() {
        this.mesh = new THREE.Sprite(this.material);
        this.mesh.geometry.setAttribute('lifetime', this.lifetimes);
        this.mesh.geometry.setAttribute('timeOffset', this.timeOffsets);
        this.mesh.geometry.setAttribute('randoms', this.randoms); // Pass randomness attribute to geometry
        this.mesh.frustumCulled = false;
        this.mesh.count = this.properties.count;

        this.scene.add(this.mesh);
    }

    setDebug() {
        if (!this.debug.active) return;

        this.debugFolder.add(this.properties, 'size', 0.01, 0.5).step(0.01).name('Size').onChange((value) => {
            this.uSize.value = value;
        });

        this.debugFolder.addColor(this.properties, 'color').name('Color').onChange((value) => {
            this.uColor.value.set(value);
        });
    }
}